import { useLocation, useNavigate } from "react-router-dom";

// components imports
import CardContainer from "../../_utils/CommonViewAllCard/CardContainer";
import TextWithIcon from "../../_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";
import AccountNumberWithBank from "../../_utils/CommonViewAllCard/CardSubComponent/AccountNumberWithBank";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import TickerAnimation from "../../_utils/TickerAnimation";
import CardActionableButton from "../../_utils/CommonViewAllCard/CardSubComponent/CardActionableButton";
import FormatAmount from "../../_utils/FormatAmount/FormatAmount";

const ScheduleIntTxnViewAllCard = ({
  openTxnHistory,
  data,
  txnHistorytype,
  currentStatus,
  onClose,
  filterRef,
  setLoading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "var(--pending)";
      case "approved":
        return "var(--awaiting)";
      case "denied":
      case "failed":
      case "rejected":
        return "var(--failed)";
      case "abandoned":
        return "var(--abandoned)";
      case "hold":
      case "queued":
        return "var(--queued)";
      default:
        return "var(--settled)";
    }
  };

  const getFormattedFrequency = (txn) => {
    const startDate = txn?.transaction_start_date;
    const frequency = txn?.frequency?.toLowerCase();

    if (!frequency) return "";

    if (!startDate && !["daily"].includes(frequency)) return "";

    const dateObj = startDate ? new Date(startDate) : null;

    const day = dateObj?.getDate();
    const weekday = dateObj?.toLocaleString("en-IN", { weekday: "short" });
    const month = dateObj?.toLocaleString("en-IN", { month: "short" });
    const year = dateObj?.getFullYear();

    const formatShortDate = () => `${day} ${month}`;
    const formatFullDate = () => `${day} ${month} ${year}`;

    switch (frequency) {
      case "weekly":
        // Weekly, on Mon
        return `on ${weekday}`;

      case "yearly":
        // Yearly, on 30 Mar
        return `on ${formatShortDate()}`;

      case "monthly":
      case "quarterly":
      case "half_yearly":
      case "fortnightly":
        // Quarterly, from 5 Jan 2025
        return `from ${formatFullDate()}`;

      case "once":
        // Once, on 30 Mar 2025
        return `on ${formatFullDate()}`;

      case "daily":
        // Daily → no date text
        return "";

      default:
        return "";
    }
  };

  const shouldBeRedacted = (price) => {
    return (
      price === "xxxx.xx" ||
      isNaN(+price) ||
      price === null ||
      price === undefined
    );
  };

  return (
    <div className={`standard-card-alignment  gap-x-gap-sm gap-y-gap-md`}>
      {data?.map((transactions) => {
        const freq = transactions?.frequency?.toLowerCase();

        const formattedFreq =
          freq === "half_yearly"
            ? "Half yearly"
            : freq
              ? freq.charAt(GlobalConfiguration.lengthCheckZero).toUpperCase() +
                freq.slice(GlobalConfiguration.lengthCheckOne)
              : "";

        const formattedDate = getFormattedFrequency(transactions);

        const frequencyText = formattedDate
          ? `${formattedFreq}, ${formattedDate}`
          : formattedFreq;
        const getApprovalStatusText = (txn, currentStatus) => {
          if (txn?.lifecycle_status === "deleted") return "Discarded";
          if (currentStatus === "Require approval") return "Require approval";
          return txn?.lifecycle_status || "N/A";
        };

        const statusText = getApprovalStatusText(transactions, currentStatus);
        return (
          <CardContainer
            filterRef={filterRef}
            hoverHeight={
              GlobalConfiguration?.internalScheduleTxnCardHoverHeight
            }
            key={transactions?.txn_id}
            onClickHandler={() => {
              setLoading(true);
              if (txnHistorytype === "Schedule_Internal_Txn") onClose()?.call();

              navigate(
                `/internal_transaction_dashboard/schedule_view_all/schedule_detail_page/${transactions.txn_id}`,
                {
                  state: {
                    isViewAll:
                      location.pathname ===
                      "/internal_transaction_dashboard/schedule_view_all"
                        ? true
                        : false,
                  },
                },
              );
            }}
          >
            <card-body>
              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p
                    className="text-font-sm flex-1 truncate font-normal"
                    style={{
                      textTransform: "capitalize",
                      color: "var(--text-primary)",
                    }}
                  >
                    <TickerAnimation text={transactions?.scheduled_name} />
                  </p>

                  <div
                    className="gap-x-gap-sm ml-margin-sm justify-end"
                    style={{
                      display:
                        transactions?.confidential_transaction === "Y"
                          ? "flex"
                          : "none",
                    }}
                  >
                    {transactions?.confidential_transaction === "Y" && (
                      <CardActionableButton
                        icon="https://q2pay.in/new_assets/common/detail_page/confidential_transaction.svg"
                        title="confidential"
                        isDisabled={false}
                      />
                    )}
                  </div>
                </div>

                <p className="text-font-xs transform-none text-text-secondary">
                  Schedule name
                </p>
              </div>

              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p
                    className="text-font-sm flex-1 truncate font-semibold"
                    style={{
                      color: "var(--settled)",
                    }}
                  >
                    {!shouldBeRedacted(transactions?.amount) && "INR"}{" "}
                    <FormatAmount
                      price={Number(
                        String(transactions?.amount)?.replaceAll(",", ""),
                      )}
                    />
                  </p>
                </div>

                <p className="text-font-xs transform-none text-text-secondary">
                  Amount
                </p>
              </div>

              <AccountNumberWithBank
                sourceAccNo={transactions?.owner_account_bank_acc_no}
                destinationAccNo={transactions?.internal_account_bank_acc_no}
                destinationIfsc={transactions?.internal_account_ifsc_code}
                sourceIfsc={transactions?.owner_account_ifsc_code}
              />

              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p
                    className="text-font-sm flex-1 truncate font-normal"
                    style={{
                      color: "var(--text-primary)",
                    }}
                  >
                    <TickerAnimation text={frequencyText || "N/A"} />
                  </p>
                </div>

                <p className="text-font-xs transform-none text-text-secondary">
                  Frequency
                </p>
              </div>
            </card-body>
            <card-hover>
              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p
                    className="text-font-sm flex-1 truncate font-normal"
                    style={{
                      textTransform: "capitalize",
                      color:
                        transactions?.lifecycle_status === "deleted"
                          ? "var(--abandoned)"
                          : getStatusColor(transactions?.lifecycle_status),
                    }}
                  >
                    {statusText}
                  </p>
                </div>
                <p className="text-font-xs transform-none text-text-secondary">
                  Approval status
                </p>
              </div>
            </card-hover>
          </CardContainer>
        );
      })}
    </div>
  );
};

export default ScheduleIntTxnViewAllCard;
